// useScheduleUrlState — wraps react-router useSearchParams and exposes
// typed getters/setters so every visible state of the schedule page is
// reproducible from the URL (pasting a link reproduces the view
// exactly). Every param is type-guarded; unknown/garbage values fall
// back to the documented default rather than throwing.
//
// History semantics: filter changes (status / contracts / hide / q /
// focus / activity) use replace so the back button isn't polluted;
// view/date navigation (view / period / zoom / range) uses push so the
// user can step back through it. historyActionFor() exposes the
// intended action so callers (and the test) can assert the contract
// without driving the History API.
//
// Defaults on first visit (no params): view=calendar, zoom=month,
// hide=['kpi'], all contracts (contracts undefined), no
// status/focus/activity/period/range/q.

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

export type ScheduleView = "calendar" | "density";
export type ScheduleZoom = "month" | "week" | "day";

export interface ScheduleUrlState {
  view: ScheduleView;
  zoom: ScheduleZoom;
  hide: string[];
  contracts?: string[];
  status?: string;
  focus?: string;
  activity?: string;
  period?: string;
  range?: string;
  q?: string;
}

// Params whose changes should NOT pollute history (filter mutations).
//
// `activity` is intentionally NOT a static member here: its history
// action is CONTEXTUAL, decided per-transition in `setActivity` (see
// the comment there) — PUSH only when opening the drawer from closed
// (so browser-back closes it, spec §14.2), REPLACE when paging
// prev/next while it is already open (so §10.5 paging doesn't pollute
// history — preserving the original anti-pollution intent that placed
// `activity` here in Phase 1, when there was no drawer to open/close).
// `focus` stays here — it is a scroll/selection hint, not navigation.
const REPLACE_PARAMS = new Set(["status", "contracts", "hide", "q", "focus"]);

type HistoryAction = "replace" | "push";

function isView(value: string | null): value is ScheduleView {
  return value === "calendar" || value === "density";
}

function isZoom(value: string | null): value is ScheduleZoom {
  return value === "month" || value === "week" || value === "day";
}

function splitCsv(value: string | null): string[] | undefined {
  if (value === null) return undefined;
  const parts = value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : undefined;
}

export interface UseScheduleUrlState {
  state: ScheduleUrlState;
  setView: (next: ScheduleView) => void;
  setPeriod: (next: string | undefined) => void;
  setRange: (next: string | undefined) => void;
  setZoom: (next: ScheduleZoom) => void;
  setContracts: (next: string[] | undefined) => void;
  setHide: (next: string[]) => void;
  setStatus: (next: string | undefined) => void;
  setFocus: (next: string | undefined) => void;
  setQ: (next: string | undefined) => void;
  setActivity: (next: string | undefined) => void;
  resetFilters: () => void;
  historyActionFor: (param: string) => HistoryAction;
}

export function useScheduleUrlState(): UseScheduleUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<ScheduleUrlState>(() => {
    const viewParam = searchParams.get("view");
    const zoomParam = searchParams.get("zoom");
    const hideParam = splitCsv(searchParams.get("hide"));

    return {
      view: isView(viewParam) ? viewParam : "calendar",
      zoom: isZoom(zoomParam) ? zoomParam : "month",
      // Absent hide param ⇒ show every kind out-of-the-box. The user
      // explicitly hides via the SHOW chips. (Was ["kpi"] — flagged as
      // confusing in feedback batch 2: users couldn't see KPI on first
      // load, and the previous backend ignored the toggle anyway.)
      hide: hideParam ?? [],
      contracts: splitCsv(searchParams.get("contracts")),
      status: searchParams.get("status") ?? undefined,
      focus: searchParams.get("focus") ?? undefined,
      activity: searchParams.get("activity") ?? undefined,
      period: searchParams.get("period") ?? undefined,
      range: searchParams.get("range") ?? undefined,
      q: searchParams.get("q") ?? undefined,
    };
  }, [searchParams]);

  // Static-introspection of a param's history action. `activity` is
  // DELIBERATELY rejected here: its history action is per-transition
  // (push on open-from-closed, replace on change-while-open / close —
  // see `setActivity`), so there is NO single static answer. Returning
  // a flat value (e.g. 'push') would silently lie for the change/close
  // transitions and break this hook's documented "introspection and
  // behaviour cannot drift" invariant. A throw makes that contract
  // un-violable: any future generic consumer/test-author gets an
  // explicit, self-explaining failure instead of a misleading value.
  // (Verified safe: every current caller passes a specific literal
  // key — no caller iterates params generically through this, and
  // none asks for `activity` — so a throw cannot break an existing
  // generic iteration. `focus` + the five committed-pinned params are
  // unchanged.)
  const historyActionFor = useCallback((param: string): HistoryAction => {
    if (param === "activity") {
      throw new Error(
        "historyActionFor('activity') is undefined: activity's history " +
          "action is per-transition (push on open-from-closed, replace " +
          "on change-while-open/close). See setActivity in " +
          "useScheduleUrlState.",
      );
    }
    return REPLACE_PARAMS.has(param) ? "replace" : "push";
  }, []);

  // Generic single-param writer. An undefined/empty value deletes the
  // param. Replace-vs-push is decided by historyActionFor so the
  // introspection and the actual behaviour cannot drift.
  const writeParam = useCallback(
    (param: string, value: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === undefined || value === "") {
            next.delete(param);
          } else {
            next.set(param, value);
          }
          return next;
        },
        { replace: historyActionFor(param) === "replace" },
      );
    },
    [setSearchParams, historyActionFor],
  );

  const setView = useCallback(
    (next: ScheduleView) => writeParam("view", next),
    [writeParam],
  );
  const setPeriod = useCallback(
    (next: string | undefined) => writeParam("period", next),
    [writeParam],
  );
  const setRange = useCallback(
    (next: string | undefined) => writeParam("range", next),
    [writeParam],
  );
  const setZoom = useCallback(
    (next: ScheduleZoom) => writeParam("zoom", next),
    [writeParam],
  );
  const setContracts = useCallback(
    (next: string[] | undefined) =>
      writeParam("contracts", next && next.length > 0 ? next.join(",") : undefined),
    [writeParam],
  );
  const setHide = useCallback(
    (next: string[]) =>
      writeParam("hide", next.length > 0 ? next.join(",") : undefined),
    [writeParam],
  );
  const setStatus = useCallback(
    (next: string | undefined) => writeParam("status", next),
    [writeParam],
  );
  const setFocus = useCallback(
    (next: string | undefined) => writeParam("focus", next),
    [writeParam],
  );
  const setQ = useCallback(
    (next: string | undefined) => writeParam("q", next),
    [writeParam],
  );
  // `activity` history is CONTEXTUAL (per-transition), not a static
  // REPLACE_PARAMS membership — this reconciles spec §14.2
  // (browser-back closes a freshly-opened drawer) with the Phase-1
  // anti-pollution intent (the hook header's "back button isn't
  // polluted" rationale) under §10.5 prev/next paging:
  //   • OPEN  (no ?activity= yet → set one): PUSH — one history entry,
  //     so a single browser-back closes the just-opened drawer.
  //   • CHANGE (?activity= already present → different one): REPLACE —
  //     prev/next paging and chip-to-chip-with-drawer-open do NOT
  //     stack N back-entries (which would trap the back button on the
  //     page).
  //   • CLOSE (strip ?activity=): REPLACE the strip itself; because
  //     OPEN was a single push, browser-back also resolves to the
  //     closed state (one back = one close). X/Esc/click-outside strip
  //     reach the same closed state.
  const setActivity = useCallback(
    (nextRaw: string | undefined) => {
      const next = nextRaw === "" ? undefined : nextRaw;
      const isOpening =
        next !== undefined && searchParams.get("activity") === null;
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next === undefined) sp.delete("activity");
          else sp.set("activity", next);
          return sp;
        },
        // Only the open-from-closed transition pushes a history entry.
        { replace: !isOpening },
      );
    },
    [setSearchParams, searchParams],
  );

  // Clears filters (status / contracts / q / focus / activity) and
  // restores hide to its default; preserves view / period / zoom /
  // range. Replace history — a reset is a filter mutation.
  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("status");
        next.delete("contracts");
        next.delete("q");
        next.delete("focus");
        next.delete("activity");
        next.delete("hide");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return {
    state,
    setView,
    setPeriod,
    setRange,
    setZoom,
    setContracts,
    setHide,
    setStatus,
    setFocus,
    setQ,
    setActivity,
    resetFilters,
    historyActionFor,
  };
}
